/**
 * Artifacts — API Callers Page (Issue #616, #636)
 *
 * List of detected API callers (frontend components / services that make API calls).
 * Left: searchable list of unique callers grouped by (sourceFile, sourceContext).
 * Right: selected caller's API calls (expandable inline) + linked user interactions.
 *
 * Each API call row expands to show: matched backend endpoint(s), linked interactions,
 * and a "View endpoint →" button that preserves breadcrumb context.
 *
 * Breadcrumb: if navigated from the endpoints page, a "← Back to GET /path" crumb appears.
 *
 * All data from the existing analysis-results endpoint — no AI calls.
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { ConfidenceBadge } from '../components/shared/ConfidenceBadge';

interface ApiRoute {
  id: string;
  methods: string[];
  path: string;
  handlerFunction: string;
  framework: string;
  sourceFile: string;
  sourceLine: number;
  confidence?: 'deterministic' | 'heuristic';
  confidenceReason?: string;
}

interface ApiCall {
  id: string;
  method: string;
  endpoint: string;
  isDynamicEndpoint: boolean;
  library: string;
  sourceFile: string;
  sourceLine: number;
  sourceContext?: string;
  wrapperFunction?: string;
  evidence?: Array<{ filePath: string; lineStart: number; lineEnd: number; snippet?: string }>;
  confidence?: 'deterministic' | 'heuristic';
  confidenceReason?: string;
}

interface UserInteraction {
  id: string;
  name: string;
  type: string;
  triggerElement: string;
  handlerFunction: string;
  handlerFile: string;
  handlerLine: number;
  component?: string;
  associatedApiCalls: string[];
  stateChanges?: string[];
  evidence?: Array<{ filePath: string; lineStart: number; lineEnd: number; snippet?: string }>;
  confidence?: 'deterministic' | 'heuristic';
  confidenceReason?: string;
}

interface CallerInfo {
  key: string;        // `${sourceFile}|${sourceContext}`
  sourceFile: string;
  sourceContext: string;
  displayName: string;
  shortFile: string;
  calls: ApiCall[];
  interactions: UserInteraction[];
  endpointCount: number;
}

const METHOD_COLORS: Record<string, string> = {
  GET:    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  POST:   'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  PUT:    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  PATCH:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  WS:     'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  SSE:    'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
};

function MethodBadge({ method }: { method: string }) {
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded font-mono ${METHOD_COLORS[method.toUpperCase()] ?? 'bg-slate-100 text-slate-700'}`}>
      {method.toUpperCase()}
    </span>
  );
}

function shortPath(filePath: string): string {
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts.slice(-2).join('/');
}

function buildRouteRegex(path: string): RegExp {
  const escaped = path
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/:[^/]+/g, '[^/]+')
    .replace(/\\\*/g, '.*');
  return new RegExp(`^${escaped}$`, 'i');
}

function normalizePath(path: string): string {
  return path
    .replace(/^\$\{[^}]+\}/, '')         // strip leading ${BASE_URL} variable
    .replace(/^\/api\//, '/')             // strip /api/ prefix
    .replace(/\$\{[^}]+\}/g, '_p_')      // ${varName} → fixed token
    .replace(/\/+$/, '');                 // strip trailing slashes
}

function callMatchesRoute(call: ApiCall, route: ApiRoute): boolean {
  const rawCallPath = call.endpoint.split('?')[0].split('#')[0];
  const normalizedCall = normalizePath(rawCallPath);
  const normalizedRoute = normalizePath(route.path);
  if (normalizedCall === normalizedRoute) return true;
  try { return buildRouteRegex(normalizedRoute).test(normalizedCall); } catch { return false; }
}

/** Derive unique callers from apiCalls + link interactions */
function deriveCallers(apiCalls: ApiCall[], interactions: UserInteraction[]): CallerInfo[] {
  const callerMap = new Map<string, CallerInfo>();

  for (const call of apiCalls) {
    const key = `${call.sourceFile}|${call.sourceContext ?? ''}`;
    if (!callerMap.has(key)) {
      callerMap.set(key, {
        key,
        sourceFile: call.sourceFile,
        sourceContext: call.sourceContext ?? '',
        displayName: call.sourceContext || shortPath(call.sourceFile),
        shortFile: shortPath(call.sourceFile),
        calls: [],
        interactions: [],
        endpointCount: 0,
      });
    }
    callerMap.get(key)!.calls.push(call);
  }

  for (const caller of callerMap.values()) {
    caller.endpointCount = new Set(caller.calls.map(c => `${c.method}:${c.endpoint}`)).size;
  }

  const callIdToCallerKey = new Map<string, string>();
  for (const caller of callerMap.values()) {
    for (const call of caller.calls) callIdToCallerKey.set(call.id, caller.key);
  }

  for (const interaction of interactions) {
    const matchedKeys = new Set<string>();
    for (const callId of interaction.associatedApiCalls) {
      const ck = callIdToCallerKey.get(callId);
      if (ck) matchedKeys.add(ck);
    }
    for (const caller of callerMap.values()) {
      if (
        interaction.handlerFile === caller.sourceFile ||
        interaction.handlerFile?.includes(caller.shortFile)
      ) {
        matchedKeys.add(caller.key);
      }
    }
    for (const ck of matchedKeys) {
      const caller = callerMap.get(ck);
      if (caller && !caller.interactions.some(i => i.id === interaction.id)) {
        caller.interactions.push(interaction);
      }
    }
  }

  return [...callerMap.values()].sort((a, b) => b.calls.length - a.calls.length);
}

// ============= Expanded Call Section =============

function ExpandedCallSection({
  call,
  allEndpoints,
  callerInteractions,
  projectId,
  callerLabel,
  callerBackUrl,
  stitchMapping,
}: {
  call: ApiCall;
  allEndpoints: ApiRoute[];
  callerInteractions: UserInteraction[];
  projectId: string;
  callerLabel: string;
  callerBackUrl: string;
  stitchMapping?: { routeId: string; method: string; path: string; framework: string; confidence: number; source: string } | null;
}) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const fromState = (state as { from?: { label: string; url: string } } | null)?.from;
  // Issue #647: Build breadcrumb for E2E flows deep links
  const flowsBreadcrumb = [
    ...(fromState ? [{ label: 'API Endpoints', url: fromState.url }] : []),
    { label: 'API Callers', url: callerBackUrl },
  ];

  // Issue #863 (epic #831): When the call has a stitch binding the
  // stitched route is authoritative — skip the regex fallback so the
  // UI doesn't show two competing answers. The regex matcher only
  // runs when no stitch exists for this call site.
  const matchedEndpoints = useMemo(() => {
    if (stitchMapping) return [];
    return allEndpoints.filter(ep => callMatchesRoute(call, ep));
  }, [allEndpoints, call, stitchMapping]);

  const linkedInteractions = useMemo(
    () => callerInteractions.filter(ia => ia.associatedApiCalls.includes(call.id)),
    [callerInteractions, call]
  );

  return (
    <div className="mt-1 ml-2 pl-3 border-l-2 border-green-200 dark:border-green-800 space-y-3">
      {/* Full file + evidence */}
      <div>
        <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">File</span>
        <p className="font-mono text-xs text-slate-600 dark:text-slate-300 mt-0.5 break-all">
          {call.sourceFile}:{call.sourceLine}
        </p>
      </div>

      {/* Issue #864 Phase B.5: Stitch-resolved endpoint (takes priority) */}
      {stitchMapping && (
        <div className="mb-2" data-testid="stitch-resolved-endpoint">
          <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">
            Stitched Endpoint
            <span className={`ml-1 px-1 py-0.5 rounded text-[10px] ${
              stitchMapping.source === 'user' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' :
              stitchMapping.source === 'ai' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' :
              'bg-green-100 dark:bg-green-900/30 text-green-600'
            }`} data-testid="stitch-badge">{stitchMapping.source}</span>
          </span>
          <div className="mt-1 flex items-center gap-2 p-1.5 rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <MethodBadge method={stitchMapping.method} />
            <span className="font-mono text-xs text-slate-700 dark:text-slate-300 flex-1 truncate">{stitchMapping.path}</span>
            <span className="text-[10px] text-slate-400">{stitchMapping.framework}</span>
          </div>
        </div>
      )}

      {/* Matched backend endpoints — issue #863: only rendered when
          no stitch exists for this call. Stitched calls show the
          authoritative endpoint above and skip this section. */}
      {!stitchMapping && (
      <div>
        <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide">
          Matched Endpoint{matchedEndpoints.length !== 1 ? 's' : ''}
          {matchedEndpoints.length > 0 && (
            <span
              className="ml-1 px-1 py-0.5 rounded text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700"
              title="Regex fallback — resolve in stitch review for accurate results"
            >
              regex
            </span>
          )}
        </span>
        {matchedEndpoints.length === 0 ? (
          <p className="text-xs text-slate-400 mt-0.5 italic">No matching route found</p>
        ) : (
          <div className="mt-1 space-y-1">
            {matchedEndpoints.map(ep => (
              <div key={ep.id} className="flex items-center gap-2 p-1.5 rounded bg-blue-50 dark:bg-blue-900/20">
                <div className="flex gap-1">
                  {ep.methods.map(m => <MethodBadge key={m} method={m} />)}
                </div>
                <span className="font-mono text-xs text-slate-700 dark:text-slate-300 flex-1 truncate">{ep.path}</span>
                <button
                  onClick={() => navigate(
                    `/projects/${projectId}/endpoints?endpointId=${ep.id}`,
                    { state: { from: { label: callerLabel, url: callerBackUrl } } }
                  )}
                  className="flex-shrink-0 inline-flex items-center gap-0.5 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  View
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Linked interactions for this specific call — prominently highlighted (Bug #639) */}
      <div className="rounded-lg border border-purple-200 dark:border-purple-700 overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/30 border-b border-purple-200 dark:border-purple-700">
          <svg className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide">
            Triggered by
          </span>
          <span className="ml-auto text-xs text-purple-500 dark:text-purple-400 font-medium">
            {linkedInteractions.length === 0 ? 'none found' : linkedInteractions.length}
          </span>
        </div>
        {linkedInteractions.length === 0 ? (
          <p className="text-xs text-slate-400 italic px-3 py-2">No direct interaction link detected</p>
        ) : (
          <div className="divide-y divide-purple-100 dark:divide-purple-800">
            {linkedInteractions.map(ia => (
              <Link
                key={ia.id}
                to={`/projects/${projectId}/flows?flowId=${ia.id}`}
                state={{ breadcrumb: flowsBreadcrumb }}
                className="flex items-start gap-2 px-3 py-2 bg-white dark:bg-gray-800 text-xs hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
              >
                <span className="mt-0.5 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded text-[10px] font-semibold flex-shrink-0 uppercase">
                  {ia.type}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800 dark:text-slate-100 font-semibold truncate">{ia.name}</div>
                  {ia.triggerElement && (
                    <div className="text-slate-500 dark:text-slate-400 mt-0.5">
                      <span className="font-mono">{ia.triggerElement}</span>
                      {ia.component && <span> in {ia.component}</span>}
                    </div>
                  )}
                </div>
                <svg className="w-3 h-3 text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= Caller Detail Panel =============

function CallerDetailPanel({
  caller,
  allEndpoints,
  projectId,
  stitchMappings,
}: {
  caller: CallerInfo;
  allEndpoints: ApiRoute[];
  projectId: string;
  stitchMappings?: { callerToEndpoint: Record<string, any> } | null;
}) {
  const { pathname, state } = useLocation();
  const fromState = (state as { from?: { label: string; url: string } } | null)?.from;
  const [expandedCallIds, setExpandedCallIds] = useState<Set<string>>(new Set());

  const callerBackUrl = `${pathname}?file=${encodeURIComponent(caller.sourceFile)}&context=${encodeURIComponent(caller.sourceContext)}`;

  // Issue #647: Build breadcrumb for E2E flows deep links
  const flowsBreadcrumb = [
    ...(fromState ? [{ label: 'API Endpoints', url: fromState.url }] : []),
    { label: 'API Callers', url: callerBackUrl },
  ];

  // Reset expansion when caller changes
  useEffect(() => { setExpandedCallIds(new Set()); }, [caller.key]);

  function toggleExpand(callId: string) {
    setExpandedCallIds(prev => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId);
      else next.add(callId);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      {/* Caller metadata */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100 mb-1">
          {caller.displayName}
        </h2>
        <p className="text-xs font-mono text-slate-500 dark:text-slate-400 break-all">{caller.sourceFile}</p>
        <div className="mt-3 flex gap-4 text-sm">
          <div>
            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{caller.calls.length}</span>
            <span className="ml-1 text-slate-500 dark:text-slate-400 text-xs">API calls</span>
          </div>
          <div>
            <span className="text-2xl font-bold text-green-600 dark:text-green-400">{caller.endpointCount}</span>
            <span className="ml-1 text-slate-500 dark:text-slate-400 text-xs">unique endpoints</span>
          </div>
          <div>
            <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">{caller.interactions.length}</span>
            <span className="ml-1 text-slate-500 dark:text-slate-400 text-xs">interactions</span>
          </div>
        </div>
      </div>

      {/* API Calls list — expandable */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            API Calls
            <span className="ml-2 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded-full text-xs text-slate-500 dark:text-slate-400">
              {caller.calls.length}
            </span>
          </h3>
          {expandedCallIds.size > 0 && (
            <button
              onClick={() => setExpandedCallIds(new Set())}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Collapse all
            </button>
          )}
        </div>
        <div className="space-y-1">
          {caller.calls.map(call => {
            const isExpanded = expandedCallIds.has(call.id);
            return (
              <div key={call.id} className="rounded-lg border border-slate-100 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => toggleExpand(call.id)}
                  className={`w-full flex items-center gap-2 p-2.5 transition-colors text-left ${
                    isExpanded
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <MethodBadge method={call.method} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 font-mono text-xs text-slate-700 dark:text-slate-300">
                      <span className="truncate">{call.endpoint}</span>
                      <ConfidenceBadge confidence={call.confidence} reason={call.confidenceReason} />
                    </div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                      {call.library !== 'unknown' && <span>{call.library} · </span>}
                      <span className="font-mono">:{call.sourceLine}</span>
                    </div>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 bg-slate-50 dark:bg-slate-800/50">
                    <ExpandedCallSection
                      call={call}
                      allEndpoints={allEndpoints}
                      callerInteractions={caller.interactions}
                      projectId={projectId}
                      callerLabel={caller.displayName}
                      callerBackUrl={callerBackUrl}
                      stitchMapping={stitchMappings?.callerToEndpoint?.[call.id] ?? null}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Linked interactions */}
      {caller.interactions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            User Interactions
            <span className="ml-2 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded-full text-xs text-slate-500 dark:text-slate-400">
              {caller.interactions.length}
            </span>
          </h3>
          <div className="space-y-2">
            {caller.interactions.map(interaction => (
              <Link
                key={interaction.id}
                to={`/projects/${projectId}/flows?flowId=${interaction.id}`}
                state={{ breadcrumb: flowsBreadcrumb }}
                className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group"
              >
                <div className="mt-0.5 p-1 rounded-md bg-purple-50 dark:bg-purple-900/30 text-purple-500 flex-shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-700 dark:text-slate-300 font-medium truncate">
                    {interaction.name || interaction.handlerFunction || 'Unnamed interaction'}
                  </div>
                  <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                    {interaction.type}
                    {interaction.triggerElement && ` · ${interaction.triggerElement}`}
                  </div>
                </div>
                <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============= Main Page =============

export function ArtifactsCallersPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fromState = (location.state as { from?: { label: string; url: string } } | null)?.from;

  const [projectName, setProjectName] = useState('');
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [interactions, setInteractions] = useState<UserInteraction[]>([]);
  const [apiEndpoints, setApiEndpoints] = useState<ApiRoute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Issue #864 Phase B.5: stitch-resolved caller→endpoint mappings
  const [stitchMappings, setStitchMappings] = useState<{
    callerToEndpoint: Record<string, { routeId: string; method: string; path: string; framework: string; confidence: number; source: string }>;
  } | null>(null);

  // Support pre-selecting a caller from the endpoints page via URL params
  const preSelectFile = searchParams.get('file');
  const preSelectContext = searchParams.get('context');

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        const [project, results] = await Promise.all([
          api.getProject(projectId),
          api.getAnalysisResults(projectId),
        ]);
        if (cancelled) return;
        setProjectName(project.name);
        setApiCalls(results.apiCalls as ApiCall[]);
        setInteractions(results.interactions as UserInteraction[]);
        setApiEndpoints(results.apiEndpoints as ApiRoute[]);
        // Issue #864 Phase B.5: load stitch-resolved mappings
        api.getStitchedMappings(projectId)
          .then(m => { if (!cancelled) setStitchMappings(m); })
          .catch(() => {}); // Non-fatal
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [projectId]);

  const callers = useMemo(() => deriveCallers(apiCalls, interactions), [apiCalls, interactions]);

  // Auto-select from URL params
  useEffect(() => {
    if (!preSelectFile || callers.length === 0) return;
    const key = `${preSelectFile}|${preSelectContext ?? ''}`;
    if (callers.some(c => c.key === key)) setSelectedKey(key);
  }, [preSelectFile, preSelectContext, callers]);

  const filteredCallers = useMemo(() => {
    if (!searchQuery.trim()) return callers;
    const q = searchQuery.toLowerCase();
    return callers.filter(c =>
      c.displayName.toLowerCase().includes(q) ||
      c.sourceFile.toLowerCase().includes(q)
    );
  }, [callers, searchQuery]);

  const selectedCaller = useMemo(
    () => callers.find(c => c.key === selectedKey) ?? null,
    [callers, selectedKey]
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link
            to={`/projects/${projectId}`}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="flex-1 min-w-0">
            {/* Breadcrumb trail */}
            <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 flex-wrap">
              <Link to={`/projects/${projectId}`} className="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">{projectName}</Link>
              <span>/</span>
              <span className="text-slate-600 dark:text-slate-300 font-medium">API Callers</span>
              {fromState && (
                <>
                  <span className="mx-1 text-slate-300 dark:text-slate-600">·</span>
                  <button
                    onClick={() => navigate(fromState.url)}
                    className="inline-flex items-center gap-1 text-blue-500 dark:text-blue-400 hover:underline"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to {fromState.label}
                  </button>
                </>
              )}
            </div>
            <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100 leading-tight mt-0.5">
              API Callers
              {!isLoading && (
                <span className="ml-2 text-sm font-normal text-slate-400">({callers.length})</span>
              )}
            </h1>
          </div>
        </div>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center flex-1">
          <svg className="animate-spin h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}

      {error && (
        <div className="p-6">
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
        </div>
      )}

      {!isLoading && !error && (
        <div className="flex flex-1 overflow-hidden">
          {/* Left panel — callers list */}
          <div className="w-80 xl:w-96 flex-shrink-0 border-r border-slate-200 dark:border-gray-700 flex flex-col overflow-hidden bg-white dark:bg-gray-800">
            <div className="p-3 border-b border-slate-200 dark:border-gray-700">
              <input
                type="text"
                placeholder="Search by name or file..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredCallers.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400 dark:text-slate-500">
                  {callers.length === 0
                    ? 'No API callers detected. Run an analysis first.'
                    : 'No callers match your search.'}
                </div>
              ) : (
                filteredCallers.map(caller => (
                  <button
                    key={caller.key}
                    onClick={() => setSelectedKey(caller.key === selectedKey ? null : caller.key)}
                    className={`w-full text-left px-3 py-3 border-b border-slate-100 dark:border-gray-700 transition-colors ${
                      selectedKey === caller.key
                        ? 'bg-green-50 dark:bg-green-900/20 border-l-2 border-l-green-500'
                        : 'hover:bg-slate-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate">
                      {caller.displayName}
                    </div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">
                      {caller.shortFile}
                    </div>
                    <div className="flex gap-3 mt-1.5">
                      <span className="text-xs text-blue-600 dark:text-blue-400">
                        {caller.calls.length} call{caller.calls.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-green-600 dark:text-green-400">
                        {caller.endpointCount} endpoint{caller.endpointCount !== 1 ? 's' : ''}
                      </span>
                      {caller.interactions.length > 0 && (
                        <span className="text-xs text-purple-600 dark:text-purple-400">
                          {caller.interactions.length} interaction{caller.interactions.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right panel — detail */}
          <div className="flex-1 overflow-y-auto p-6">
            {selectedCaller ? (
              <CallerDetailPanel
                caller={selectedCaller}
                allEndpoints={apiEndpoints}
                projectId={projectId!}
                stitchMappings={stitchMappings}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500 gap-3">
                <svg className="w-10 h-10 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                <p className="text-sm">Select a caller to see its API calls and linked interactions</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
